import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  CreditCard,
  Package,
  DollarSign,
  Edit,
  Plus,
  Trash2,
  Download,
  RefreshCw,
  Search,
  Settings2,
  Infinity,
} from "lucide-react";

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  monthly_credits: number;
  price_cents: number;
  is_active: boolean;
  is_free: boolean;
  features: string[];
  sort_order: number;
}

interface ActionCreditCost {
  id: string;
  action_type: string;
  credit_cost: number;
  display_name: string;
  description: string | null;
  is_active: boolean;
}

interface CreditTransaction {
  id: string;
  user_id: string;
  action_type: string;
  credits_change: number;
  credits_before: number;
  credits_after: number;
  description: string | null;
  created_at: string;
  user_email?: string;
}

interface UserPayment {
  id: string;
  user_id: string;
  plan_id: string | null;
  amount_cents: number;
  payment_status: string;
  created_at: string;
  user_email?: string;
  plan_name?: string;
}

interface PlanCreditOverride {
  id: string;
  plan_id: string;
  action_type: string;
  credit_cost: number;
  is_unlimited: boolean;
  plan_name?: string;
}

export default function AdminBilling() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [actionCosts, setActionCosts] = useState<ActionCreditCost[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [planOverrides, setPlanOverrides] = useState<PlanCreditOverride[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Dialog state
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [editingCost, setEditingCost] = useState<ActionCreditCost | null>(null);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [editingOverride, setEditingOverride] = useState<PlanCreditOverride | null>(null);
  const [showAddOverride, setShowAddOverride] = useState(false);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      // Fetch plans
      const { data: plansData } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("sort_order");

      setPlans((plansData || []).map(p => ({
        ...p,
        features: Array.isArray(p.features) ? (p.features as string[]) : []
      })));

      // Fetch costs
      const { data: costsData } = await supabase
        .from("action_credit_costs")
        .select("*")
        .order("display_name");

      setActionCosts(costsData || []);

      // Fetch transactions
      const { data: txData } = await supabase
        .from("credit_transactions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      // Fetch profiles for emails
      const { data: profiles } = await supabase.from("profiles").select("id, email");

      const txWithEmails = (txData || [])
        .filter((tx) => profiles?.some((p) => p.id === tx.user_id))
        .map((tx) => ({
          ...tx,
          user_email: profiles?.find((p) => p.id === tx.user_id)?.email || "Unknown",
        }));
      setTransactions(txWithEmails);

      // Fetch payments
      const { data: paymentsData } = await supabase
        .from("user_payments")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      const paymentsWithInfo = (paymentsData || []).map((p) => ({
        ...p,
        user_email: profiles?.find((pr) => pr.id === p.user_id)?.email || "Unknown",
        plan_name: plansData?.find((pl) => pl.id === p.plan_id)?.name || "N/A",
      }));
      setPayments(paymentsWithInfo);

      // Fetch plan credit overrides
      const { data: overridesData } = await supabase
        .from("plan_credit_overrides")
        .select("*")
        .order("plan_id");

      const overridesWithNames = (overridesData || []).map((o) => ({
        ...o,
        plan_name: plansData?.find((p) => p.id === o.plan_id)?.name || "Unknown",
      }));
      setPlanOverrides(overridesWithNames);
    } catch (error) {
      console.error("Error fetching billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  const handleSavePlan = async (plan: Partial<SubscriptionPlan>) => {
    try {
      if (editingPlan?.id) {
        const { error } = await supabase
          .from("subscription_plans")
          .update(plan)
          .eq("id", editingPlan.id);
        if (error) throw error;
        toast({ title: "Plan updated" });
      } else {
        const { error } = await supabase.from("subscription_plans").insert([plan as any]);
        if (error) throw error;
        toast({ title: "Plan created" });
      }
      setEditingPlan(null);
      setShowAddPlan(false);
      fetchBillingData();
    } catch (error: any) {
      toast({ title: "Error saving plan", description: error.message, variant: "destructive" });
    }
  };

  const handleSaveCost = async (cost: Partial<ActionCreditCost>) => {
    try {
      const { error } = await supabase
        .from("action_credit_costs")
        .update(cost)
        .eq("id", editingCost?.id);
      if (error) throw error;
      toast({ title: "Cost updated" });
      setEditingCost(null);
      fetchBillingData();
    } catch (error: any) {
      toast({ title: "Error saving cost", description: error.message, variant: "destructive" });
    }
  };

  const handleSaveOverride = async (override: Partial<PlanCreditOverride>) => {
    try {
      if (editingOverride?.id) {
        const { error } = await supabase
          .from("plan_credit_overrides")
          .update({
            credit_cost: override.credit_cost,
            is_unlimited: override.is_unlimited,
          })
          .eq("id", editingOverride.id);
        if (error) throw error;
        toast({ title: "Override updated" });
      } else {
        const { error } = await supabase
          .from("plan_credit_overrides")
          .insert([{
            plan_id: override.plan_id,
            action_type: override.action_type,
            credit_cost: override.credit_cost,
            is_unlimited: override.is_unlimited,
          }]);
        if (error) throw error;
        toast({ title: "Override created" });
      }
      setEditingOverride(null);
      setShowAddOverride(false);
      fetchBillingData();
    } catch (error: any) {
      toast({ title: "Error saving override", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteOverride = async (id: string) => {
    try {
      const { error } = await supabase.from("plan_credit_overrides").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Override deleted" });
      fetchBillingData();
    } catch (error: any) {
      toast({ title: "Error deleting override", variant: "destructive" });
    }
  };

  const handleTogglePlanActive = async (planId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("subscription_plans")
        .update({ is_active: isActive })
        .eq("id", planId);
      if (error) throw error;
      fetchBillingData();
    } catch (error: any) {
      toast({ title: "Error updating plan", variant: "destructive" });
    }
  };

  const exportTransactionsCSV = () => {
    const filtered = transactions.filter(
      (tx) =>
        tx.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.action_type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const headers = ["Date", "User", "Action", "Change", "Before", "After", "Description"];
    const rows = filtered.map((tx) => [
      new Date(tx.created_at).toISOString(),
      tx.user_email,
      tx.action_type,
      tx.credits_change,
      tx.credits_before,
      tx.credits_after,
      tx.description || "",
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredTransactions = transactions.filter(
    (tx) =>
      tx.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.action_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Credits & Billing</h1>
          <p className="text-muted-foreground">Manage plans, pricing, and transactions</p>
        </div>
        <Button variant="outline" onClick={fetchBillingData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">
            <Package className="h-4 w-4 mr-2" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="costs">
            <CreditCard className="h-4 w-4 mr-2" />
            Credit Costs
          </TabsTrigger>
          <TabsTrigger value="transactions">
            <DollarSign className="h-4 w-4 mr-2" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="payments">
            <DollarSign className="h-4 w-4 mr-2" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="overrides">
            <Settings2 className="h-4 w-4 mr-2" />
            Plan Overrides
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddPlan(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Plan
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <Card key={plan.id} className={!plan.is_active ? "opacity-60" : ""}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {plan.name}
                        {plan.is_free && <Badge variant="secondary">Free</Badge>}
                      </CardTitle>
                      <CardDescription>{plan.description}</CardDescription>
                    </div>
                    <Switch
                      checked={plan.is_active}
                      onCheckedChange={(checked) => handleTogglePlanActive(plan.id, checked)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{formatCurrency(plan.price_cents)}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {plan.monthly_credits} credits/month
                  </div>
                  {plan.features.length > 0 && (
                    <ul className="text-sm space-y-1">
                      {plan.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                          {f}
                        </li>
                      ))}
                      {plan.features.length > 4 && (
                        <li className="text-muted-foreground">+{plan.features.length - 4} more...</li>
                      )}
                    </ul>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setEditingPlan(plan)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs">
          <Card>
            <CardHeader>
              <CardTitle>Action Credit Costs</CardTitle>
              <CardDescription>Configure how many credits each action costs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actionCosts.map((cost) => (
                    <TableRow key={cost.id}>
                      <TableCell className="font-mono text-sm">{cost.action_type}</TableCell>
                      <TableCell>{cost.display_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{cost.credit_cost}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={cost.is_active ? "default" : "outline"}>
                          {cost.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditingCost(cost)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Credit Transactions</CardTitle>
                  <CardDescription>All credit changes across users</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Button variant="outline" onClick={exportTransactionsCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.slice(0, 100).map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(tx.created_at)}
                      </TableCell>
                      <TableCell>{tx.user_email}</TableCell>
                      <TableCell>
                        {tx.action_type.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                      </TableCell>
                      <TableCell>
                        <Badge variant={tx.credits_change > 0 ? "default" : "secondary"}>
                          {tx.credits_change > 0 ? "+" : ""}{tx.credits_change}
                        </Badge>
                      </TableCell>
                      <TableCell>{tx.credits_after}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {tx.description || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>All user payments and subscriptions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No payments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(p.created_at)}
                        </TableCell>
                        <TableCell>{p.user_email}</TableCell>
                        <TableCell>{p.plan_name}</TableCell>
                        <TableCell>{formatCurrency(p.amount_cents)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              p.payment_status === "completed"
                                ? "default"
                                : p.payment_status === "pending"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {p.payment_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plan Overrides Tab */}
        <TabsContent value="overrides">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Plan Credit Overrides</CardTitle>
                  <CardDescription>
                    Configure per-plan pricing. Set unlimited for free tools on higher tiers, or custom costs per plan.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAddOverride(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Override
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {planOverrides.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No plan overrides configured.</p>
                  <p className="text-sm">All plans use the universal credit costs above.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Override Cost</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {planOverrides.map((override) => {
                      const defaultCost = actionCosts.find((c) => c.action_type === override.action_type)?.credit_cost || 0;
                      return (
                        <TableRow key={override.id}>
                          <TableCell>
                            <Badge variant="outline">{override.plan_name}</Badge>
                          </TableCell>
                          <TableCell>
                            {actionCosts.find((c) => c.action_type === override.action_type)?.display_name || override.action_type}
                          </TableCell>
                          <TableCell>
                            {override.is_unlimited ? (
                              <Badge variant="secondary" className="gap-1">
                                <Infinity className="h-3 w-3" />
                                Unlimited
                              </Badge>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">{override.credit_cost}</Badge>
                                {override.credit_cost !== defaultCost && (
                                  <span className="text-xs text-muted-foreground line-through">
                                    (default: {defaultCost})
                                  </span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={override.is_unlimited ? "default" : "secondary"}>
                              {override.is_unlimited ? "Free" : "Discounted"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button variant="ghost" size="sm" onClick={() => setEditingOverride(override)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteOverride(override.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Plan Dialog */}
      {(editingPlan || showAddPlan) && (
        <PlanEditDialog
          plan={editingPlan}
          onSave={handleSavePlan}
          onClose={() => {
            setEditingPlan(null);
            setShowAddPlan(false);
          }}
        />
      )}

      {/* Edit Cost Dialog */}
      {editingCost && (
        <CostEditDialog
          cost={editingCost}
          onSave={handleSaveCost}
          onClose={() => setEditingCost(null)}
        />
      )}

      {/* Override Dialog */}
      {(editingOverride || showAddOverride) && (
        <OverrideEditDialog
          override={editingOverride}
          plans={plans}
          actionCosts={actionCosts}
          existingOverrides={planOverrides}
          onSave={handleSaveOverride}
          onClose={() => {
            setEditingOverride(null);
            setShowAddOverride(false);
          }}
        />
      )}
    </div>
  );
}

// Plan Edit Dialog Component
function PlanEditDialog({
  plan,
  onSave,
  onClose,
}: {
  plan: SubscriptionPlan | null;
  onSave: (plan: Partial<SubscriptionPlan>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    name: plan?.name || "",
    description: plan?.description || "",
    monthly_credits: plan?.monthly_credits || 10,
    price_cents: plan?.price_cents || 0,
    is_active: plan?.is_active ?? true,
    is_free: plan?.is_free ?? false,
    sort_order: plan?.sort_order || 0,
    features: plan?.features || [],
  });
  const [newFeature, setNewFeature] = useState("");

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature("");
    }
  };

  const removeFeature = (index: number) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan ? "Edit Plan" : "Add Plan"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Monthly Credits</Label>
              <Input type="number" value={formData.monthly_credits} onChange={(e) => setFormData({ ...formData, monthly_credits: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Price (cents)</Label>
              <Input type="number" value={formData.price_cents} onChange={(e) => setFormData({ ...formData, price_cents: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sort Order</Label>
              <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: Number(e.target.value) })} />
            </div>
            <div className="flex items-center gap-4 pt-6">
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_free} onCheckedChange={(c) => setFormData({ ...formData, is_free: c })} />
                <Label>Free</Label>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Features</Label>
            <div className="space-y-2 bg-muted/50 p-3 rounded-md max-h-40 overflow-y-auto">
              {formData.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={f} onChange={(e) => {
                    const updated = [...formData.features];
                    updated[i] = e.target.value;
                    setFormData({ ...formData, features: updated });
                  }} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeFeature(i)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input placeholder="Add feature..." value={newFeature} onChange={(e) => setNewFeature(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} />
                <Button type="button" variant="outline" size="sm" onClick={addFeature}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Cost Edit Dialog Component
function CostEditDialog({
  cost,
  onSave,
  onClose,
}: {
  cost: ActionCreditCost;
  onSave: (cost: Partial<ActionCreditCost>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    credit_cost: cost.credit_cost,
    display_name: cost.display_name,
    description: cost.description || "",
    is_active: cost.is_active,
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Credit Cost</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Display Name</Label>
            <Input value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>
          <div>
            <Label>Credit Cost</Label>
            <Input type="number" min={0} value={formData.credit_cost} onChange={(e) => setFormData({ ...formData, credit_cost: Number(e.target.value) })} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
            <Label>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Override Edit Dialog Component
function OverrideEditDialog({
  override,
  plans,
  actionCosts,
  existingOverrides,
  onSave,
  onClose,
}: {
  override: PlanCreditOverride | null;
  plans: SubscriptionPlan[];
  actionCosts: ActionCreditCost[];
  existingOverrides: PlanCreditOverride[];
  onSave: (override: Partial<PlanCreditOverride>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    plan_id: override?.plan_id || "",
    action_type: override?.action_type || "",
    credit_cost: override?.credit_cost ?? 0,
    is_unlimited: override?.is_unlimited ?? false,
  });

  // Filter out already-configured combinations when adding new
  const availableActions = override
    ? actionCosts
    : actionCosts.filter(
        (cost) =>
          !existingOverrides.some(
            (o) => o.plan_id === formData.plan_id && o.action_type === cost.action_type
          )
      );

  const selectedActionDefault = actionCosts.find((c) => c.action_type === formData.action_type)?.credit_cost || 0;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{override ? "Edit Override" : "Add Plan Override"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!override && (
            <>
              <div>
                <Label>Plan</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.plan_id}
                  onChange={(e) => setFormData({ ...formData, plan_id: e.target.value, action_type: "" })}
                >
                  <option value="">Select a plan...</option>
                  {plans.filter((p) => p.is_active).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Action</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.action_type}
                  onChange={(e) => {
                    const selectedCost = actionCosts.find((c) => c.action_type === e.target.value)?.credit_cost || 0;
                    setFormData({ ...formData, action_type: e.target.value, credit_cost: selectedCost });
                  }}
                  disabled={!formData.plan_id}
                >
                  <option value="">Select an action...</option>
                  {availableActions.filter((c) => c.is_active).map((c) => (
                    <option key={c.action_type} value={c.action_type}>
                      {c.display_name} (default: {c.credit_cost} credits)
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {override && (
            <div className="bg-muted p-3 rounded-md text-sm">
              <p><strong>Plan:</strong> {override.plan_name}</p>
              <p><strong>Action:</strong> {actionCosts.find((c) => c.action_type === override.action_type)?.display_name || override.action_type}</p>
              <p className="text-muted-foreground">Default cost: {selectedActionDefault} credits</p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_unlimited}
              onCheckedChange={(c) => setFormData({ ...formData, is_unlimited: c })}
            />
            <Label>Unlimited (Free for this plan)</Label>
          </div>

          {!formData.is_unlimited && (
            <div>
              <Label>Override Credit Cost</Label>
              <Input
                type="number"
                min={0}
                value={formData.credit_cost}
                onChange={(e) => setFormData({ ...formData, credit_cost: Number(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Default: {selectedActionDefault} credits. Set a lower value for discounts.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!override && (!formData.plan_id || !formData.action_type)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
